// All occupancy/clash/report calculation and id->name mapping now happens
// server-side in lms-api's classroommanagement module (see
// ClassroomManagementService.js). This file keeps only pure client-side
// form-input utilities that aren't business logic.

// `.toISOString()` reads the date back in UTC — for a browser running east
// of UTC (IST, this app's timezone) that's wrong for part of the day: from
// midnight to 5:30am local time, the UTC calendar date is still "yesterday",
// so this would report the wrong "today" overnight. Reading local getters
// instead keeps it the actual local calendar date, always. Same fix as
// `eachDateStrInRange` below, and classroommanagement.service.js's backend
// counterpart — same root cause (local-midnight Date, read back via UTC).
const formatLocalDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const todayDateStr = () => formatLocalDateStr(new Date());

// "YYYY-MM-DD" moved by `n` calendar days -- the timelines' Prev / Next day.
export const shiftDateStr = (dateStr, n) => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + n);
  return formatLocalDateStr(d);
};

export const minutesToTimeValue = (min) => {
  const h = Math.floor(min / 60).toString().padStart(2, "0");
  const m = (min % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
};

// The same minutes, for reading: 12-hour with AM/PM ("9:30 AM"). The popups
// print busy and free times with this; minutesToTimeValue above stays the
// 24-hour "HH:MM" that <input type="time"> and the booking form need.
// 1440 is the end of the day, which reads as "Midnight" rather than as a
// second "12:00 AM" beside the one that starts it.
export const minutesToTimeLabel = (min) => {
  if (min >= 1440) return "Midnight";
  const h = Math.floor(min / 60) % 24;
  const m = String(min % 60).padStart(2, "0");
  return `${h % 12 || 12}:${m} ${h < 12 ? "AM" : "PM"}`;
};

export const combineDateAndTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  return new Date(`${dateStr}T${timeStr}:00`).toISOString();
};

// Every "YYYY-MM-DD" date from fromStr to toStr inclusive — used to turn a
// from/to date filter into the day-by-day grid columns on the lab & trainer
// timelines (see GridTimeline.jsx) whenever the picked range spans more
// than a single day.
export const eachDateStrInRange = (fromStr, toStr) => {
  const dates = [];
  let cursor = new Date(`${fromStr}T00:00:00`);
  const end = new Date(`${toStr}T00:00:00`);
  while (cursor <= end) {
    dates.push(formatLocalDateStr(cursor));
    cursor = new Date(cursor.getTime() + 86400000);
  }
  return dates;
};

export const formatDateLabel = (dateStr, opts) =>
  new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-US", opts || { month: "short", day: "numeric", year: "numeric" });

// Readable label for a grid cell on the Labs/Trainers multi-day timeline
// (see GridTimeline.jsx) — the actual batch name(s) running that day,
// instead of a bare session count. Cells truncate with an ellipsis (see
// classroomApp.css) rather than wrapping, so this just picks how much text
// is worth handing over; the full list always still shows in the cell's
// title/tooltip.
export const sessionsCellLabel = (sessions) => {
  const names = [...new Set((sessions || []).map((s) => s.batchName).filter(Boolean))];
  if (!names.length) return "";
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1} more`;
};

// Gaps in a single day's already-sorted session list (grid-cell popups: see
// openRoomForDate/openTrainerForDate in DashboardTab.jsx) — mirrors
// classroommanagement.service.js's `computeFreeWindows` so a day drilled
// into from the multi-day grid shows the same "when's it actually free"
// section the single-day view gets straight from the API.
export const computeFreeWindows = (daySessions) => {
  const freeWindows = [];
  let cursor = 0;
  (daySessions || []).forEach((s) => {
    if (s.startMin > cursor) freeWindows.push({ startMin: cursor, endMin: s.startMin });
    cursor = Math.max(cursor, s.endMin);
  });
  if (cursor < 1440) freeWindows.push({ startMin: cursor, endMin: 1440 });
  return freeWindows;
};
